/**
 * Location Popup Component
 * Displays business/organization highlights in a popup overlay
 */

import React, { useState } from 'react';
import { ChatLocation } from '@/types/chatTypes';
import {
  spacing,
  typography,
  typographyColors,
  fontPrimary,
  backgroundColors,
  borders,
  brandColors,
} from '@/design-system/utils';
import { Chip, Badge } from '@/design-system';
import ImageCarousel from '@/components/common/ImageCarousel';
import { CloseButton } from '@/components/common/CloseButton';

export interface LocationPopupProps {
  location: ChatLocation;
  onClose: () => void;
}

export const LocationPopup: React.FC<LocationPopupProps> = ({
  location,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'hours'>('overview');

  // Format opening hours
  const formatOpeningHours = (hours: string[] | null | undefined) => {
    if (!hours || hours.length === 0) return null;
    return hours;
  };

  // Get current day status
  const getCurrentDayStatus = () => {
    if (location.open_now === null || location.open_now === undefined) {
      return null;
    }
    return location.open_now ? 'Open now' : 'Closed now';
  };

  // Format categories for display
  const formatCategories = (types: string[] | undefined) => {
    if (!types || types.length === 0) return [];
    // Filter out generic types and format nicely
    return types
      .filter(
        type =>
          !['point_of_interest', 'establishment'].includes(type.toLowerCase())
      )
      .map(type => type.replace(/_/g, ' '))
      .slice(0, 6);
  };

  // Get location details
  const getLocationInfo = () => {
    const parts = [];
    if (location.locality) parts.push(location.locality);
    if (location.administrative_area_level_1)
      parts.push(location.administrative_area_level_1);
    if (location.country) parts.push(location.country);
    return parts.join(', ');
  };

  const openingHours = formatOpeningHours(location.opening_hours_text);
  const currentStatus = getCurrentDayStatus();
  const categories = formatCategories(location.types);
  const locationInfo = getLocationInfo();
  // Handle both image_urls (snake_case) and imageUrls (camelCase) from API
  const images =
    location.image_urls ||
    (location as unknown as { imageUrls?: string[] }).imageUrls ||
    [];
  const hasImages = images.length > 0;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
          cursor: 'pointer',
        }}
      />

      {/* Popup */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: backgroundColors.primary,
          borderRadius: borders.radius.lg,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          zIndex: 9999,
          maxWidth: '320px',
          width: '320px',
          maxHeight: '80vh',
          overflow: 'auto',
          cursor: 'default',
        }}
      >
        {/* Header with image slider */}
        {hasImages && (
          <div
            style={{
              width: '100%',
              height: '250px',
              position: 'relative',
              borderTopLeftRadius: borders.radius.lg,
              borderTopRightRadius: borders.radius.lg,
              overflow: 'hidden',
            }}
          >
            <ImageCarousel
              key={
                location.place_id ||
                location.place_name ||
                location.title ||
                location.place ||
                'popup'
              }
              images={images}
              alt={
                location.title ||
                location.place ||
                location.place_name ||
                'Location image'
              }
              className='w-full h-full'
              imageClassName='object-cover'
              showNavigation={false}
              showPagination={images.length > 1}
              autoplay={images.length > 1}
              loop={images.length > 1}
              slidesPerView={1}
              spaceBetween={0}
              enableFullscreen={false}
              fullscreenTitle={
                location.title || location.place || location.place_name || ''
              }
            />
          </div>
        )}

        {/* Tabs - Only show if opening hours are available */}
        {location.opening_hours_text &&
          location.opening_hours_text.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: spacing[1],
                borderBottom: `${borders.width.thin} solid ${backgroundColors.shade}`,
                paddingLeft: spacing[3],
                paddingRight: spacing[3],
              }}
              className='flex-shrink-0 z-10 bg-white'
            >
              <button
                onClick={() => setActiveTab('overview')}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom:
                    activeTab === 'overview'
                      ? `2px solid ${brandColors.primary}`
                      : 'none',
                  padding: `${spacing[1.5]} ${spacing[2]}`,
                  fontFamily: fontPrimary,
                  fontSize: typography.size.sm,
                  fontWeight:
                    activeTab === 'overview'
                      ? typography.weight.semibold
                      : typography.weight.regular,
                  color: typographyColors.primary,
                  cursor: 'pointer',
                  borderRadius: activeTab === 'overview' ? '2px 2px 0 0' : '0',
                }}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('hours')}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom:
                    activeTab === 'hours'
                      ? `2px solid ${brandColors.primary}`
                      : 'none',
                  padding: `${spacing[1.5]} ${spacing[2]}`,
                  fontFamily: fontPrimary,
                  fontSize: typography.size.sm,
                  fontWeight:
                    activeTab === 'hours'
                      ? typography.weight.semibold
                      : typography.weight.regular,
                  color: typographyColors.primary,
                  cursor: 'pointer',
                  borderRadius: activeTab === 'hours' ? '2px 2px 0 0' : '0',
                }}
              >
                Hours
              </button>
            </div>
          )}

        {/* Content */}
        <div style={{ padding: spacing[3] }}>
          {/* Close button */}
          <div
            style={{
              position: 'absolute',
              top: spacing[4],
              right: spacing[4],
              zIndex: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.4)',
              borderRadius: '50%',
              padding: spacing[0.5],
            }}
          >
            <CloseButton
              onClick={onClose}
              size={16}
              ariaLabel='Close popup'
              variant='default'
            />
          </div>

          {/* Overview Tab - Show always if no tabs, or conditionally if tabs exist */}
          <div
            style={{
              display:
                location.opening_hours_text &&
                location.opening_hours_text.length > 0
                  ? activeTab === 'overview'
                    ? 'block'
                    : 'none'
                  : 'block',
            }}
          >
            {/* Title */}
            <h2
              style={{
                fontFamily: fontPrimary,
                fontSize: typography.size.xl,
                fontWeight: typography.weight.semibold,
                color: typographyColors.primary,
                marginBottom: spacing[2],
                marginTop: hasImages ? 0 : spacing[4],
                paddingRight: spacing[8],
              }}
            >
              {location.title || location.place || location.place_name}
            </h2>

            {/* Location Info */}
            {locationInfo && (
              <p
                style={{
                  fontFamily: fontPrimary,
                  fontSize: typography.size.sm,
                  color: typographyColors.secondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[2],
                  margin: 0,
                  marginBottom: spacing[2],
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                <span style={{ flexShrink: 0 }}>🌍</span>
                <span style={{ minWidth: 0 }}>{locationInfo}</span>
              </p>
            )}

            {/* Current Status - New Row */}
            {currentStatus && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[1],
                  marginBottom: spacing[2],
                }}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor:
                      location.open_now === true ? '#10b981' : '#ef4444',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontFamily: fontPrimary,
                    fontSize: typography.size.sm,
                    fontWeight: typography.weight.medium,
                    color: location.open_now === true ? '#10b981' : '#ef4444',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {currentStatus}
                </span>
              </div>
            )}

            {/* Business Status */}
            {location.business_status &&
              location.business_status !== 'OPERATIONAL' && (
                <div style={{ marginBottom: spacing[4] }}>
                  <Badge
                    variant='warning'
                    size='md'
                    style={{
                      display: 'block',
                      padding: spacing[3],
                      width: '100%',
                    }}
                  >
                    <strong>⚠️ Notice:</strong> This business is currently{' '}
                    {location.business_status.toLowerCase().replace(/_/g, ' ')}
                  </Badge>
                </div>
              )}

            {/* Categories */}
            {categories.length > 0 && (
              <div style={{ marginBottom: spacing[4] }}>
                <h3
                  style={{
                    fontFamily: fontPrimary,
                    fontSize: typography.size.sm,
                    fontWeight: typography.weight.semibold,
                    color: typographyColors.primary,
                    marginBottom: spacing[2],
                  }}
                >
                  Categories
                </h3>
                <div
                  style={{ display: 'flex', flexWrap: 'wrap', gap: spacing[2] }}
                >
                  {categories.map((type, index) => (
                    <Chip
                      key={index}
                      label={type}
                      size='sm'
                      variant='outlined'
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Hours Tab */}
          {openingHours && openingHours.length > 0 && (
            <div style={{ display: activeTab === 'hours' ? 'block' : 'none' }}>
              <h3
                style={{
                  fontFamily: fontPrimary,
                  fontSize: typography.size.sm,
                  fontWeight: typography.weight.semibold,
                  color: typographyColors.primary,
                  marginBottom: spacing[2],
                  marginTop: hasImages ? 0 : spacing[4],
                }}
              >
                Opening Hours
              </h3>
              <div>
                {openingHours.map((hours, index) => (
                  <p
                    key={index}
                    style={{
                      fontFamily: fontPrimary,
                      fontSize: typography.size.sm,
                      color: typographyColors.secondary,
                      marginBottom: spacing[1],
                    }}
                  >
                    {hours}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
